//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Type 87 validation checks
//
//  Copyright:    © 2024 - 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.1.0
//    001   31.10.24 Sean Flook       IMANN-1012 Initial Revision.
//endregion Version 1.0.1.0
//region Version 1.0.3.0
//    002   06.01.25 Sean Flook       IMANN-1121 Fixed logic for failsCheck8700004.
//endregion Version 1.0.3.0
//region Version 1.0.7.0
//    003   29.10.25 Sean Flook       IMANN-1812 Added check 8700032 - Thumbnail Url is too long.
//    004   06.11.25 Sean Flook       IMANN-2092 Fix for check 8700030.
//    005   24.11.25 Sean Flook       IMANN-2147 Changes required for validation.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import MapLayerTypes from "../data/MapLayerTypes";

/**
 * Check 8700004 - Layer type is invalid.
 *
 * @param {number|null} layerType The map layer type
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700004 = (layerType) => {
  return layerType && !MapLayerTypes.find((x) => x.id === layerType);
};

/**
 * Check 8700005 - Title is too long.
 *
 * @param {string|null} title The map layer title
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700005 = (title) => {
  return title && title.length > 40;
};

/**
 * Check 8700006 - Copyright is too long.
 *
 * @param {string|null} copyright The map layer copyright
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700006 = (copyright) => {
  return copyright && copyright.length > 100;
};

/**
 * Check 8700007 - Service provider is too long.
 *
 * @param {string|null} serviceProvider The map layer service provider
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700007 = (serviceProvider) => {
  return serviceProvider && serviceProvider.length > 50;
};

/**
 * Check 8700008 - Layer key is too long.
 *
 * @param {string|null} layerKey The map layer key
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700008 = (layerKey) => {
  return layerKey && layerKey.length > 40;
};

/**
 * Check 8700009 - Layer username is too long.
 *
 * @param {string|null} layerUsername The map layer username
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700009 = (layerUsername) => {
  return layerUsername && layerUsername.length > 40;
};

/**
 * Check 8700010 - Layer password is too long.
 *
 * @param {string|null} layerPassword The map layer password
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700010 = (layerPassword) => {
  return layerPassword && layerPassword.length > 40;
};

/**
 * Check 8700011 - Service layer name is too long.
 *
 * @param {string|null} activeLayerId The map layer service layer
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700011 = (activeLayerId) => {
  return activeLayerId && activeLayerId.length > 40;
};

/**
 * Check 8700012 - Service mode is too long.
 *
 * @param {string|null} serviceMode The map layer service mode
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700012 = (serviceMode) => {
  return serviceMode && serviceMode.length > 40;
};

/**
 * Check 8700013 - Property name is too long.
 *
 * @param {string|null} propertyName The map layer property name
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700013 = (propertyName) => {
  return propertyName && propertyName.length > 100;
};

/**
 * Check 8700014 - Enter a type of service.
 *
 * @param {number|null} layerType The map layer type
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700014 = (layerType) => {
  return !layerType;
};

/**
 * Check 8700015 - Enter a service layer name.
 *
 * @param {number|null} layerType The map layer type
 * @param {string|null} activeLayerId The map layer service layer
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700015 = (layerType, activeLayerId) => {
  return layerType !== 4 && !activeLayerId;
};

/**
 * Check 8700018 - Enter a URL.
 *
 * @param {string|null} url The map layer url
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700018 = (url) => {
  return !url;
};

/**
 * Check 8700019 - Opacity is invalid.
 *
 * @param {string|null} opacity The map layer opacity
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700019 = (opacity) => {
  return !opacity;
};

/**
 * Check 8700020 - Enter a service provider.
 *
 * @param {string|null} serviceProvider The map layer service provider
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700020 = (serviceProvider) => {
  return !serviceProvider;
};

/**
 * Check 8700022 - Enter a copyright.
 *
 * @param {string|null} serviceProvider The map layer copyright
 * @param {string|null} activeLayerId The map layer service layer
 * @param {string|null} copyright The map layer copyright
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700022 = (serviceProvider, activeLayerId, copyright) => {
  return (
    (serviceProvider === "OS" ||
      serviceProvider === "viaEuropa" ||
      (serviceProvider === "thinkWare" && activeLayerId && activeLayerId.toLowerCase().startsWith("osmm"))) &&
    !copyright
  );
};

/**
 * Check 8700023 - Enter a service mode.
 *
 * @param {string|null} serviceProvider The map layer service provider
 * @param {number|null} layerType The map layer type
 * @param {string|null} serviceMode The map layer service mode
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700023 = (serviceProvider, layerType, serviceMode) => {
  return serviceProvider === "OS" && layerType === 3 && !serviceMode;
};

/**
 * Check 8700024 - Enter a property name.
 *
 * @param {number|null} layerType The map layer type
 * @param {string|null} propertyName The map layer property name
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700024 = (layerType, propertyName) => {
  return layerType === 1 && !propertyName;
};

/**
 * Check 8700027 - Enter a layer key.
 *
 * @param {string|null} serviceProvider The map layer service provider
 * @param {string|null} layerKey The map layer key
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700027 = (serviceProvider, layerKey) => {
  return (serviceProvider === "OS" || serviceProvider === "viaEuropa") && !layerKey;
};

/**
 * Check 8700028 - Title is missing.
 *
 * @param {string|null} title The map layer title
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700028 = (title) => {
  return !title;
};

/**
 * Check 8700030 - Enter a geometry type.
 *
 * @param {number|null} layerType The map layer type
 * @param {string|null} geometryType The map layer geometry type
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700030 = (layerType, geometryType) => {
  return layerType < 3 && !geometryType;
};

/**
 * Check 8700031 - Enter a map layer id.
 *
 * @param {string|null} layerId The map layer id
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700031 = (layerId) => {
  return !layerId;
};

/**
 * Check 8700032 - Thumbnail Url is too long.
 * @param {string|null} thumbnailUrl The map layer thumbnail url
 * @returns {boolean} True if the check fails; otherwise false.
 */
export const failsCheck8700032 = (thumbnailUrl) => {
  return thumbnailUrl && thumbnailUrl.length > 512;
};
